import styled from "styled-components";
import { breakpoint, getDisplayPriceWithOriginal } from "@utilities";
import { BookingPrice } from "@interfaces";
import { ClipLoaderCommon } from "@components";
import { theme } from "@utilities";

interface IProps {
    faresPrices: BookingPrice[];
    isLoading?: boolean;
}

export const ProductPriceMessage = ({ faresPrices, isLoading }: IProps) => {
    const { price: fromPrice, originalPrice: wasPrice } = getDisplayPriceWithOriginal(faresPrices);
    const fromPriceNum = parseFloat(fromPrice.replace("$", "")) || 0;
    const wasPriceNum = wasPrice ? parseFloat(wasPrice.replace("$", "")) || 0 : null;
    const savings = wasPriceNum && fromPriceNum > 0 ? wasPriceNum - fromPriceNum : 0;

    if (isLoading) {
        return (
            <StyledPrice className="price">
                <ClipLoaderCommon size={24} color={theme.colors.highlight} margin={0} />
            </StyledPrice>
        );
    }

    return (
        <StyledPrice className="price">
            <TopRow>
                {theme.colors.price ? (
                    <DetailPriceBadge>
                        <span className="badge-from">From</span>
                        <span className="badge-price">{fromPrice}</span>
                    </DetailPriceBadge>
                ) : (
                    <>
                        <FromLabel>From</FromLabel>
                        <CurrentPrice>{fromPrice}</CurrentPrice>
                    </>
                )}
            </TopRow>
            {wasPriceNum && savings > 0 && (
                <PriceGroup>
                    <WasPrice>was {wasPrice}</WasPrice>
                    <SaveBadge>SAVE ${savings.toFixed(2)}</SaveBadge>
                </PriceGroup>
            )}
        </StyledPrice>
    );
};

const StyledPrice = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TopRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const FromLabel = styled.span`
    font-size: 14px !important;
    font-weight: 400 !important;
    color: ${(props) => props.theme.colors.gray_3} !important;
`;

const CurrentPrice = styled.span`
    font-size: 36px;
    font-weight: 700;
    color: ${(props) => props.theme.colors.price || props.theme.colors.highlight};

    ${breakpoint.breakMobile`
        font-size: 28px;
    `}
`;

const PriceGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const WasPrice = styled.span`
    position: relative;
    font-size: 14px !important;
    font-weight: 400 !important;
    color: ${(props) => props.theme.colors.gray_3} !important;
    ${(props) =>
        props.theme.colors.price
            ? `
        text-decoration: none !important;
        &::after {
            content: '';
            position: absolute;
            left: -2px;
            right: -2px;
            top: 0;
            bottom: 0;
            background: linear-gradient(
                to top left,
                transparent calc(50% - 1px),
                ${props.theme.colors.price} calc(50% - 1px),
                ${props.theme.colors.price} calc(50% + 1px),
                transparent calc(50% + 1px)
            );
        }
    `
            : `text-decoration: line-through !important;`}
`;

const SaveBadge = styled.span`
    background-color: ${(props) => props.theme.colors.main} !important;
    color: ${(props) => props.theme.colors.button_text} !important;
    font-size: 12px !important;
    font-weight: 500 !important;
    padding: 4px 12px;
    border-radius: 999px;
`;

const DetailPriceBadge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: ${(props) => props.theme.colors.price || props.theme.colors.highlight};
    color: #fff !important;
    padding: 6px 16px;
    border-radius: 999px;
    line-height: 1;

    .badge-from {
        font-size: 12px !important;
        font-weight: 400 !important;
        color: #fff !important;
        line-height: 1 !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex;
        align-items: center;
    }

    .badge-price {
        font-size: 20px !important;
        font-weight: 700 !important;
        color: #fff !important;
        line-height: 1 !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex;
        align-items: center;

        ${breakpoint.breakMobile`
            font-size: 18px !important;
        `}
    }
`;
